import { groupBy } from "lodash";
import PGSHelper from "../../helpers/PGSHelper.js";
const pgsHelper = new PGSHelper();

export default class ExistenciasGenerales {
  constructor() {}

  static getDocDefinition(mainData, getCalculatedGlobalLimits) {
    return {
      pageMargins: [10, 10, 10, 10],
      pageOrientation: "landscape",
      content: [
        this.getBalancedHeader(mainData),
        this.getBalancedTable(
          mainData.productos,
          mainData.Origenes,
          getCalculatedGlobalLimits
        ),
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true,
          margin: [0, 0, 0, 8],
        },
        subheader: {
          fontSize: 11,
          bold: true,
          margin: [0, 5, 0, 5],
        },
        tableHeader: {
          bold: true,
          fontSize: 9,
          color: "black",
          fillColor: "#f5f5f5",
          alignment: "center",
          margin: [2, 2, 2, 2],
        },
        tableCell: {
          fontSize: 8,
          margin: [2, 2, 2, 2],
          lineHeight: 1.1,
        },
        warningText: {
          color: "red",
          fontSize: 7,
        },
        infoText: {
          fontSize: 7,
          color: "#555555",
          lineHeight: 1.0,
        },
      },
      defaultStyle: {
        lineHeight: 1.2,
      },
    };
  }

  static getBalancedHeader(mainData) {
    return [
      {
        text: "REPORTE DE EXISTENCIAS GENERALES",
        style: "header",
        alignment: "center",
      },
      {
        columns: [
          {
            text: `Generado: ${mainData.FG}`,
            style: "subheader",
            width: "auto",
          },
          {
            text: `Hora: ${mainData.HG}`,
            style: "subheader",
            width: "auto",
          },
          {
            text: `Departamento: ${mainData.dep}`,
            style: "subheader",
            width: "auto",
          },
        ],
        columnGap: 20,
      },
    ];
  }

  static getBalancedTable(productos, origenes, getCalculatedGlobalLimits) {
    // Configuración de anchos automáticos con mínimos sugeridos
    const columnWidths = [
      "auto",
      "auto",
      "auto",
      "auto",
      ...origenes.map(() => "auto"),
      "auto",
    ];

    const minColumnWidths = [
      100, // Producto
      60, // Código
      60, // Familia
      60, // Categoría
      ...origenes.map(() => 70), // Columnas de orígenes
      90, // Existencia Total
    ];

    const headers = [
      { text: "Producto (UM)", style: "tableHeader" },
      { text: "Código", style: "tableHeader" },
      { text: "Familia", style: "tableHeader" },
      { text: "Categoría", style: "tableHeader" },
      ...origenes.map((org) => ({
        text: org.nombreOrigen,
        style: "tableHeader",
        alignment: "center",
      })),
      {
        text: "Existencia Total",
        style: "tableHeader",
        alignment: "center",
      },
    ];

    const body = [headers];

    productos.forEach((prod) => {
      const row = [
        {
          text: `${prod.Nombre}\n(${prod.UnidadMedida})`,
          style: "tableCell",
        },
        {
          text: prod.Codigo,
          style: "tableCell",
        },
        {
          text: prod.Familia,
          style: "tableCell",
        },
        {
          text: prod.categoria_name,
          style: "tableCell",
        },
        ...origenes.map((org) => this.getBalancedOrigenCell(prod, org)),
        this.getBalancedTotalCell(prod, getCalculatedGlobalLimits),
      ];

      body.push(row);
    });

    return {
      table: {
        headerRows: 1,
        widths: columnWidths,
        body: body,
        layout: {
          fillColor: function (rowIndex) {
            return rowIndex === 0 ? "#f5f5f5" : null;
          },
          // Función para evitar que las columnas sean demasiado estrechas
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 3,
          paddingBottom: () => 3,
          // Asegurar mínimos sin forzar anchos fijos
          minCellWidth: (colIndex) => minColumnWidths[colIndex] || 0,
        },
      },
      margin: [0, 5, 0, 0],
    };
  }

  static getBalancedOrigenCell(producto, origen) {
    const existencia = producto.ExistenciaAlmacen?.[origen.Origen];
    const amcs = producto.amcs?.[origen.Origen];
    const stock = existencia?.Stock || 0;
    const um = producto.UnidadMedida;

    const content = [
      {
        text: pgsHelper.formatNumber(stock) + " " + um,
        style: "tableCell",
        bold: true,
      },
    ];

    if (amcs) {
      content.push({
        text: `Mín:${amcs.emin} Ideal:${amcs.eidl}`,
        style: "infoText",
      });

      if (stock < amcs.emin) {
        content.push({
          text: "BAJA EXISTENCIA",
          style: "warningText",
        });
      } else if (stock < amcs.eidl) {
        const faltante = amcs.eidl - stock;
        content.push({
          text: `Faltan ${pgsHelper.formatNumber(faltante)}`,
          style: "infoText",
        });
      }
    }

    return {
      stack: content,
      style: "tableCell",
      alignment: "center",
    };
  }

  static getBalancedTotalCell(producto, getCalculatedGlobalLimits) {
    const total = producto.ExitenciaTotal || 0;
    const um = producto.UnidadMedida;
    const limits = getCalculatedGlobalLimits(producto);

    const content = [
      {
        text: pgsHelper.formatNumber(total) + " " + um,
        style: "tableCell",
        bold: true,
      },
    ];

    if (limits) {
      content.push({
        text: `Mín:${limits.Min} Ideal:${limits.Max}`,
        style: "infoText",
      });

      if (total < limits.Min) {
        content.push({
          text: "BAJA EXISTENCIA",
          style: "warningText",
        });
      } else if (total < limits.Max) {
        const faltante = limits.Max - total;
        content.push({
          text: `Faltan ${pgsHelper.formatNumber(faltante)}`,
          style: "infoText",
        });
      }
    }

    return {
      stack: content,
      style: "tableCell",
      alignment: "center",
    };
  }
}
